<template>
  <div class="min-h-screen bg-zinc-900 flex items-center justify-center p-1 sm:p-4 font-nunito">
    <!-- Container principal -->
    <div class="w-full max-w-lg">
      <!-- Logo -->
      <div class="flex items-center justify-center mb-8">
        <img src="@/assets/bot_express_logo.png" alt="Logo" class="h-auto w-[180px] sm:w-[280px]">
      </div>

      <!-- Card de recuperação -->
      <div class="bg-white/5 rounded-xl overflow-hidden">
        <div class="p-6 sm:p-8">
          <!-- Cabeçalho -->
          <div class="mb-8">
            <h2 class="text-white text-2xl font-bold mb-2">Redefinir Senha</h2>
            <p class="text-[#888888]">Digite uma nova senha para a sua conta</p>
          </div>

          <!-- Formulário -->
          <form @submit.prevent="handleSubmit" class="space-y-5">
            <!-- Nova Senha -->
            <div class="space-y-2">
              <label for="password" class="block text-sm font-medium text-[#AAAAAA]">Nova Senha</label>
              <div class="relative">
                <input 
                  id="password" 
                  v-model="password" 
                  :type="showPassword ? 'text' : 'password'" 
                  required
                  class="w-full px-4 py-3 bg-white/5 rounded-md text-white placeholder-[#555555] focus:outline-none focus:ring-2 focus:ring-[#10B981] focus:border-[#10B981] transition-all duration-200"
                  placeholder="••••••••"
                  :class="{ 'ring-2 ring-red-500': errors.password }"
                />
                <button 
                  type="button" 
                  @click="showPassword = !showPassword" 
                  class="absolute inset-y-0 right-0 flex items-center pr-3 text-[#555555] hover:text-[#AAAAAA] transition-colors"
                >
                  <Eye v-if="!showPassword" class="w-5 h-5" />
                  <EyeOff v-else class="w-5 h-5" />
                </button>
              </div>
              <p v-if="errors.password" class="text-red-500 text-sm mt-1">{{ errors.password }}</p>
            </div>

            <!-- Confirmar Senha -->
            <div class="space-y-2">
              <label for="confirmPassword" class="block text-sm font-medium text-[#AAAAAA]">Confirmar Senha</label>
              <div class="relative">
                <input 
                  id="confirmPassword" 
                  v-model="confirmPassword" 
                  :type="showConfirmPassword ? 'text' : 'password'" 
                  required
                  class="w-full px-4 py-3 bg-white/5 rounded-md text-white placeholder-[#555555] focus:outline-none focus:ring-2 focus:ring-[#10B981] focus:border-[#10B981] transition-all duration-200"
                  placeholder="••••••••"
                  :class="{ 'ring-2 ring-red-500': errors.confirmPassword }"
                />
                <button 
                  type="button" 
                  @click="showConfirmPassword = !showConfirmPassword" 
                  class="absolute inset-y-0 right-0 flex items-center pr-3 text-[#555555] hover:text-[#AAAAAA] transition-colors"
                >
                  <Eye v-if="!showConfirmPassword" class="w-5 h-5" />
                  <EyeOff v-else class="w-5 h-5" />
                </button>
              </div>
              <p v-if="errors.confirmPassword" class="text-red-500 text-sm mt-1">{{ errors.confirmPassword }}</p>
            </div>

            <!-- Botão de enviar -->
            <button 
              type="submit" 
              class="w-full py-3 px-4 bg-[#10B981] hover:bg-[#0EA271] text-white font-medium rounded-md transition-colors duration-200 flex items-center justify-center"
              :disabled="!isValid || isLoading"
              :class="{
                'opacity-50 cursor-not-allowed': !isValid || isLoading
              }"
            >
              <Loader2 v-if="isLoading" class="w-4 h-4 animate-spin mr-2" />
              <span>Redefinir Senha</span>
              <KeyRound class="ml-2 w-4 h-4" />
            </button>
          </form>

          <!-- Link para Voltar ao login -->
          <div class="mt-8 text-center">
            <p class="text-[#888888] text-sm">
              Lembrou sua senha?
              <RouterLink to="/login" class="text-[#10B981] hover:text-[#0EA271] font-medium ml-1 transition-colors">
                Voltar ao login
              </RouterLink>
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
  <Toaster />
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { toast, Toaster } from '@/components/ui/toast'
import { useAuth } from '@/composables/useAuth'
import { Loader2, Eye, EyeOff, KeyRound } from 'lucide-vue-next'

// Estado do formulário
const props = defineProps<{ token: string }>()
const router = useRouter()
const { resetPassword } = useAuth()

const password = ref('')
const confirmPassword = ref('')
const showPassword = ref(false)
const showConfirmPassword = ref(false)
const isLoading = ref(false)
const errors = ref({
  password: '',
  confirmPassword: ''
})

const validatePassword = (password: string) => {
  return password.length >= 8
}

// Computed property para verificar se o formulário é válido
const isValid = computed(() => {
  return (
    password.value &&
    confirmPassword.value &&
    password.value === confirmPassword.value &&
    validatePassword(password.value) &&
    Object.values(errors.value).every(error => !error)
  )
})

watch(password, (newValue) => {
  if (!newValue) {
    errors.value.password = 'Senha é obrigatória'
  } else if (!validatePassword(newValue)) {
    errors.value.password = 'A senha deve ter pelo menos 8 caracteres'
  } else {
    errors.value.password = ''
  }
  
  if (confirmPassword.value && newValue !== confirmPassword.value) {
    errors.value.confirmPassword = 'As senhas não coincidem'
  } else {
    errors.value.confirmPassword = ''
  }
})

watch(confirmPassword, (newValue) => {
  if (!newValue) {
    errors.value.confirmPassword = 'Confirmação de senha é obrigatória'
  } else if (newValue !== password.value) {
    errors.value.confirmPassword = 'As senhas não coincidem'
  } else {
    errors.value.confirmPassword = ''
  }
})

// Função de submit
const handleSubmit = async () => {
  if (!isValid.value) return

  isLoading.value = true
  try {
    await resetPassword({
      token: props.token,
      password: password.value
    })

    toast({
      title: 'Senha redefinida com sucesso!',
      description: 'Você já pode fazer login com sua nova senha',
      class: 'bg-green-500 text-white',
      duration: 3000
    })

    // Limpar formulário
    password.value = ''
    confirmPassword.value = ''

    // Redirecionar para login após 2 segundos
    setTimeout(() => {
      router.push('/login')
    }, 2000)
  } catch (error) {
    toast({
      title: 'Erro ao redefinir senha',
      description: 'Tente novamente mais tarde ou solicite uma redefinição de senha novamente',
      class: 'bg-red-500 text-white',
      duration: 3000
    })
  } finally {
    isLoading.value = false
  }
}
</script>